<template>
	<div class="data-dashboard" v-if="!noAccess">
		<DxPopup :visible="showDataGrid !== null" :show-title="true" :show-close-button="true" :title="showDataGrid" :defer-rendering="true" @hidden="dataGridHidden">
			<template #content>
				<div style="height: 100%;">
					<DxDataGrid :data-source="showDataGridSource"
						:focused-row-index="0"
						:show-borders="false"
						:focused-row-enabled="true"
						:column-auto-width="true"
						:column-hiding-enabled="true"
						:allowColumnResizing="true" columnResizingMode="widget"
						height="100%"
						@exporting="exportHandler"
					>
						<dx-column-chooser :enabled="true" mode="select" :searchEnabled="true" height="400px">
							<dx-position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button" />
						</dx-column-chooser>
						<dx-paging :page-size="10" />
						<dx-pager :show-page-size-selector="true" :show-info="true" />
						<dx-filter-row :visible="true" />
						<dx-header-filter :visible="true" />
						<dx-search-panel :visible="true" width="300px" />
						<dx-toolbar>
							<dx-item location="before" name="searchPanel" />
							<dx-item name="exportButton" location="after" />
							<dx-item name="columnChooserButton" location="after" />
						</dx-toolbar>
						<dx-scrolling :useNative="true" />
						<dx-export :enabled="true" :formats="['xlsx', 'pdf']" :texts="{ exportAll: 'Export to {0}' }" />
						<dx-master-detail :enabled="true" template="detail" />
						<template #detail="{ data }">
							<applicant-detail :applicant="data.data" :remote="showDataGrid.value && typeof showDataGrid.value === 'string' && showDataGrid.value.startsWith('Brainhub') ? true : false" />
						</template>
					</DxDataGrid>
				</div>
			</template>
		</DxPopup>
		<h2 class="content-block header-container">
			<span>Data Dashboard</span>
			<DxSelectBox class="sort-selector" displayExpr="text" valueExpr="value" v-model:value="cardSortMode" @valueChanged="sortDashboardCards"
				:items="[
					{ text: 'Sort by Name', value: 'name' },
					{ text: 'Sort by First Created', value: 'first' },
					{ text: 'Sort by Recent', value: 'recent' },
					{ text: 'Sort by Largest', value: 'biggest' }
				]"
			/>
		</h2>
		<div class="content-block dx-card dashboard-sparklines" v-if="!restrictedAccess">
			<div v-if="sparklineSubmitted.length > 0" style="width: 250px;">
				<DxSparkline :data-source="sparklineSubmitted" type="area" value-field="count" argument-field="date" :show-min-max="true" :size="{ width: 250, height: 60 }" />
				<div style="text-align: center; font-size: 0.8em; font-weight: bold; margin-top: 5px;">Forms submitted last 6 months</div>
			</div>
			<div v-if="sparklineVisitors.length > 0" style="width: 250px;">
				<DxSparkline :data-source="sparklineVisitors" type="area" value-field="count" argument-field="date" :show-min-max="true" :size="{ width: 250, height: 60 }" />
				<div style="text-align: center; font-size: 0.8em; font-weight: bold; margin-top: 5px;">Visitors last 6 months</div>
			</div>
			<div v-if="sparklineRegistrations.length > 0" style="width: 250px;">
				<DxSparkline :data-source="sparklineRegistrations" type="area" value-field="count" argument-field="date" :show-min-max="true" :size="{ width: 250, height: 60 }" />
				<div style="text-align: center; font-size: 0.8em; font-weight: bold; margin-top: 5px;">Registrations last 6 months</div>
			</div>
		</div>
		<div class="content-block dashboard-grid-container">
			<DxLoadPanel shading-color="rgba(0, 0, 0, 0.2)" :visible="dashboardCards.length === 0" />
			<div v-for="card in dashboardCards" :key="card.id" :class="{ 'dx-card': true, 'responsive-paddings-small': true, 'expanded': expandedCardId === card.id }">
				<div v-if="card.id == 'placeholder:empty'" class="placeholder-card">
					<i class="dx-icon-warning"></i>
					<div class="title">No data available</div>
					<div class="description">No surveys or other data was found. At least one survey must be submitted to get started.</div>
				</div>
				<div v-else-if="card.id == 'placeholder:options'" class="options-placeholder">
					Options placeholder
				</div>
				<dashboard-surveycard v-else-if="card.id.startsWith('survey:')" :data="card" :show-months="expandedCardId === card.id ? 12 : 6"
					@expand-card="handleExpandCard" @view-data="handleViewData"
				/>
				<div v-else class="placeholder-card">
					<i class="dx-icon-warning"></i>
					<div class="title">Unknown card data</div>
					<div class="description">Data for this card (<b>{{ card.id }}</b>) was not recognized or is invalid.</div>
				</div>
			</div>
		</div>
	</div>
	<div v-else class="content-block">
		<div class="placeholder-card">
			<i class="dx-icon-warning"></i>
			<div class="title">No access</div>
			<div class="description">You do not have access to this page. Try logging in again.</div>
		</div>
	</div>
</template>

<script setup>
/* eslint-disable */
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxSparkline } from 'devextreme-vue/sparkline';
import { DxPopup } from 'devextreme-vue/popup';
import DxDataGrid, { DxFilterRow, DxPager, DxPaging, DxColumnChooser, DxPosition, DxToolbar, DxItem, DxSearchPanel, DxScrolling, DxHeaderFilter, DxExport, DxMasterDetail } from "devextreme-vue/data-grid";
import CustomStore from 'devextreme/data/data_source';
import { onMounted, inject, ref, nextTick } from 'vue';
import DashboardSurveycard from '@/components/dashboard-surveycard.vue';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { jsPDF } from 'jspdf';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import Auth from '@/auth';
import ApplicantDetail from '@/components/applicant-detail.vue';

const noAccess = ref(false);				// Simple access warning since we don't have a good way to handle this yet.
const restrictedAccess = ref(false);		// Whether the user has restricted access to the dashboard, i.e. they are an external non-staff user with access to a restricted subset of surveys.

const dashboardCards = ref([]);				// Array of cards to display on the dashboard. Each represents a survey, statistic or placeholder.
const cardSortMode = ref('biggest');		// Sort cards by 'name' or 'first' or 'recent' or 'biggest'.
const expandedCardId = ref(null);			// The ID of the card that is currently expanded.
const sparklineSubmitted = ref([]);			// Array of simple data used in sparkline for total count of forms submitted per last 6 months.
const sparklineVisitors = ref([]);			// Array of simple data used in sparkline for total count of visitors per last 6 months.	
const sparklineRegistrations = ref([]);		// Array of simple data used in sparkline for total count of registrations per last 6 months.
const showDataGrid = ref(null);				// The title of the data grid to show, or null if none.
let showDataGridSource = null;				// The DataSource for the data grid to show, or null if none.

const $http = inject('$http');
onMounted(async () => {
	// Get a list of all survey types and their counts by month updated.
	let aggregates = null;
	try {
		aggregates = await $http.get('/survey', { params: { type: '_aggregate', remotes: true } });
		restrictedAccess.value = !Auth.isStaff.value;
	} catch (error) {
		console.error('Error fetching survey aggregates:', error);
		if (error.response.status === 403 || error.response.status === 401) {
			noAccess.value = true;
			return;
		} else {
			throw error;
		}
	}
	const uniqueSurveyTypes = [...new Set(aggregates.data.map(item => item.type))];
	ensureEmptySurveys(uniqueSurveyTypes);
	// Fetch non-survey statistics (visitors and registrations).
	const numericalStats = await $http.get('/stats');
	const statVisitors = numericalStats.data && numericalStats.data.visitors ? numericalStats.data.visitors : {};
	const statRegistrations = numericalStats.data && numericalStats.data.registrations ? numericalStats.data.registrations : {};

	// For each survey type, create a card entry with the first and last dates, and count of surveys total.
	uniqueSurveyTypes.forEach(type => {
		const subdata = aggregates.data.filter(item => item.type === type);
		dashboardCards.value.push({
			id: `survey:${type}`,
			title: surveyTypeMetadata(type).title,
			icon: surveyTypeMetadata(type).icon,
			description: null,
			// Ensure subdata is sorted by date properly!
			subdata: subdata.sort((a, b) => new Date(a.month) - new Date(b.month)),
			first_created: subdata.reduce((oldest, item) => !oldest || item.first_created < oldest ? item.first_created : oldest, ''),
			last_updated: subdata.reduce((newest, item) => !newest || item.last_updated > newest ? item.last_updated : newest, ''),
			count: subdata.reduce((sum, item) => sum + item.count, 0),
		});
	});

	// Generate our sparkline data for forms submitted, users registered and visitors to site.
	const today = new Date();
	const iterateDate = new Date(today.getFullYear(), today.getMonth(), 1);
	for (var i = 0; i < 6; i++) {
		const matchingMonth = iterateDate.getFullYear() + '-' + String(iterateDate.getMonth() + 1).padStart(2, '0');
		const sumTotal = aggregates.data.filter(item => item.month == matchingMonth).reduce((sum, item) => sum + item.count, 0);
		sparklineSubmitted.value.push({ date: matchingMonth, count: sumTotal });
		sparklineVisitors.value.push({ date: matchingMonth, count: matchingMonth in statVisitors ? statVisitors[matchingMonth] : 0 });
		sparklineRegistrations.value.push({ date: matchingMonth, count: matchingMonth in statRegistrations ? statRegistrations[matchingMonth] : 0 });
		iterateDate.setMonth(iterateDate.getMonth() - 1);
	}
	sparklineSubmitted.value.sort((a, b) => new Date(a.date) - new Date(b.date));
	sparklineVisitors.value.sort((a, b) => new Date(a.date) - new Date(b.date));
	sparklineRegistrations.value.sort((a, b) => new Date(a.date) - new Date(b.date));

	// Add placeholder cards to the dashboard - one for empty data (so load indiciator is hidden automatically), or one for grid options.
	if (dashboardCards.value.length === 0) {
		dashboardCards.value.push({
			id: 'placeholder:empty',
			placement: 'first',
		});
	} else {
		/*dashboardCards.value.push({
			id: 'placeholder:options',
			placement: 'last',
		});*/
	}
	sortDashboardCards();
});

// Sort the dashboard cards by the selected sort mode.
const sortDashboardCards = () => {
	dashboardCards.value.sort((a, b) => {
		// Handle placement values first
		if (a.placement === 'first') return -1;
		if (b.placement === 'first') return 1;
		if (a.placement === 'last') return 1;
		if (b.placement === 'last') return -1;

		// Then do normal sorting for other cards
		if (cardSortMode.value === 'name') {
			return a.title.localeCompare(b.title);
		} else if (cardSortMode.value === 'first') {
			return a.first_created.localeCompare(b.first_created);
		} else if (cardSortMode.value === 'recent') {
			return b.last_updated.localeCompare(a.last_updated);
		} else if (cardSortMode.value === 'biggest') {
			return b.count - a.count;
		}
	});
};

// Handle requests to expand/contract a card.
const handleExpandCard = (cardId) => {
	expandedCardId.value = expandedCardId.value === cardId ? null : cardId;
	// Scroll to the expanded card when possible.
	nextTick(() => {
		if (expandedCardId.value) {
			const expandedCardElement = document.querySelector('.dx-card.expanded');
			if (expandedCardElement) {
				expandedCardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
	});
};

// Handle requests to view data table for a card. Only handles surveys for now.
const handleViewData = (cardId) => {
	const surveyType = cardId.split(':')[1];
	showDataGridSource = new CustomStore({
		key: 'id',
		loadMode: 'raw',
		remoteOperations: false,
		load: () => {
			const needsRemotes = surveyType.startsWith('_') ? '1' : '0';
			return $http.get(`/survey/?type=${surveyType}&remotes=${needsRemotes}`).then(response => response.data);
		}
	});
	
	showDataGrid.value = surveyTypeMetadata(surveyType).title;
};
// Cleanup when popup is closed with datagrid - reset DataSource so we can free the memory.
const dataGridHidden = () => {
	showDataGrid.value = null;
	// Doing this causes a warning when using applicant-detail component in the master detail. So we must postpone it till next tick?
	nextTick(() => {
		showDataGridSource = null;
	});
};

// Temporary function to convert survey type to metadata (title, icon) -- until we generalize our surveys.
const surveyTypeMetadata = (type) => {
	switch (type) {
		case 'nnrc14':   			return { title: "NNRC 2014 Survey", icon: '' };
		case 'general':  			return { title: "Colpuno.com General Survey", icon: '' };
		case '_bhub_application': 	return { title: "Brainhub Application", icon: '' };
		case '_bhub_survey': 		return { title: "Brainhub Applicant Survey", icon: '' };
		case '_bhub_alumni': 		return { title: "Brainhub Alumni Survey", icon: '' };
		default:         			return { title: type + " Survey", icon: '' };
	}
}
// Temporary function to ensure we have several surveys that might be empty at the moment.
const ensureEmptySurveys = (existingTypes) => {
	if (restrictedAccess.value) {
		// Don't add empty surveys here because we probably have data but don't transmit them for these.
		return;
	}
	const requiredTypes = ['nnrc14', 'general', '_bhub_application', '_bhub_survey', '_bhub_alumni' ];
	requiredTypes.forEach(type => {
		if (!existingTypes.includes(type)) {
			existingTypes.push(type);
		}
	});
};

// Handle exporting of datagrid to Excel, PDF, etc.
const exportHandler = (e) => {
	let dateCode = (new Date()).toISOString().substring(0, 10);
	if (e.format == 'xlsx') {
		const workbook = new Workbook();
		const worksheet = workbook.addWorksheet('Main sheet');
		exportDataGrid({
			component: e.component,
			worksheet: worksheet,
			customizeCell: function(options) {
				options.excelCell.font = { name: 'Arial', size: 12 };
				options.excelCell.alignment = { horizontal: 'left' };
			} 
		}).then(function() {
			workbook.xlsx.writeBuffer()
				.then(function(buffer) {
					saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Employees-' + dateCode + '.xlsx');
				});
		});
	} else if (e.format == 'pdf') {
		const doc = new jsPDF();
		exportDataGridToPdf({
			jsPDFDocument: doc,
			component: e.component
		}).then(() => {
			doc.save('Employees-' + dateCode + '.pdf');
		});
	}
};
</script>

<style scoped lang="scss">
.dashboard-grid-container {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	grid-auto-rows: auto;
	gap: 15px;

	.dx-card.expanded {
		grid-column: 1 / -1;
	}
}
.dashboard-grid-container > div:first-child {
	/* This fixes a subpixel issue with the first dx-card in grid layouts. */
	margin-top: 0;
}
.dashboard-grid-container > div {
	break-inside: avoid;
	margin-bottom: 15px;
}
.placeholder-card {
	text-align: center;
}
.placeholder-card i {
	color: var(--dx-color-danger);
	font-size: 3em;
}
.placeholder-card .title {
	font-size: 1.5em;
	font-weight: bold;
	border-bottom: 1px solid var(--dx-color-border);
	margin-bottom: 0.5em;
}
.placeholder-card .description {
	font-style: italic;
}
.header-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	gap: 1rem;
}
.sort-selector {
	min-width: 200px;
	max-width: 300px;
}

.dashboard-sparklines {
	padding: 10px 10px;
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	justify-content: center;
}
</style>
